import React, { useState, useEffect } from "react";
import { useAdminContext } from "@src/context/adminContext";
import { useAuthContext } from "@src/context";
import { useToastContext } from "@src/context/toastContext";
import AdminHooks from "@src/context/hooks/adminHooks";
import { AppUtils } from "@src/utils";
import {
  Checkbox,
  DropDown,
  Button,
  Tabs,
  ImageWithRetry,
} from "@src/components/atoms";
import { CardContainer } from "@src/components/cards";
import { CUSTOMER_LOGO_URL } from "@src/constants/constants";

/** How to resolve the recipient operatorIds for "Trigger a Report", instead of pasting UUIDs by hand. */
const SEND_TO_MODES = {
  ALL: "ALL",
  REGISTERED: "REGISTERED",
  UNREGISTERED: "UNREGISTERED",
  RECENT_EVENT_DATA: "RECENT_EVENT_DATA",
  NOT_RECENTLY_SENT: "NOT_RECENTLY_SENT",
  ROLE: "ROLE",
  MANUAL: "MANUAL",
};

const sendToOptions = [
  { value: SEND_TO_MODES.ALL, text: "All operators (default recipients)" },
  { value: SEND_TO_MODES.REGISTERED, text: "All registered users" },
  { value: SEND_TO_MODES.UNREGISTERED, text: "All unregistered users" },
  {
    value: SEND_TO_MODES.RECENT_EVENT_DATA,
    text: "Operators with event data more recent than...",
  },
  {
    value: SEND_TO_MODES.NOT_RECENTLY_SENT,
    text: "Operators who haven't received this template in the last...",
  },
  { value: SEND_TO_MODES.ROLE, text: "Operators with a given role..." },
  { value: SEND_TO_MODES.MANUAL, text: "Specific operator IDs (manual)" },
];

const emailOptions = [
  { value: "REPORT_KICKOFF", text: "Report Kickoff Aviation" },
  { value: "REPORT_KICKOFF_SHIPPING", text: "Report Kickoff Shipping" },
  { value: "REPORT_MIDMONTHLY", text: "Report Mid Period Aviation" },
  { value: "REPORT_MIDMONTHLY_SHIPPING", text: "Report Mid Period Shipping" },
  { value: "REPORT_END_PERIOD", text: "Report End Period Aviation" },
  { value: "REPORT_END_PERIOD_SHIPPING", text: "Report End Period Shipping" },
  { value: "MILESTONES", text: "Milestones Aviation" },
  { value: "MILESTONES_SHIPPING", text: "Milestones Shipping" },
  { value: "WELCOME_BACK", text: "Welcome Back Aviation" },
  { value: "WELCOME_BACK_SHIPPING", text: "Welcome Back Shipping" },
  { value: "NEW_BEHAVIOUR_ANNOUNCED", text: "New Behaviour Announced" },
  { value: "SPECIAL_UPDATES", text: "Special Updates" },
  { value: "SPECIAL_MIGRATION_UPDATES", text: "Special Migration Updates" },
  { value: "NEWUSER", text: "New User Aviation" },
  { value: "NEWUSER_SHIPPING", text: "New User Shipping" },
  { value: "NEWUSER_MANAGER", text: "New User Manager" },
  { value: "FAREWELL", text: "Farewell" },
  { value: "CHASER", text: "Chaser" },
];

function ReportsServiceSnsForm() {
  const { customers, selectedCustomer } = useAdminContext();
  const auth = useAuthContext();
  const { showToast } = useToastContext();
  const [operatorIdsHint, setOperatorIdsHint] = useState("");
  const [sendToMode, setSendToMode] = useState(SEND_TO_MODES.ALL);
  const [recentEventDataSince, setRecentEventDataSince] = useState("");
  const [notRecentlySentDays, setNotRecentlySentDays] = useState("30");
  const [selectedRoleAlias, setSelectedRoleAlias] = useState("");
  const [currentCrewOnly, setCurrentCrewOnly] = useState(false);

  const mutationCallback = (data) => {
    console.debug("mutationCallback", { data });
  };
  const mutation = AdminHooks.useTriggerReportsService(mutationCallback);

  /** Simplified version of SnsTriggerReportDto from reports service, see also formDataParsed */
  const [formData, setFormData] = React.useState({
    customerCode: selectedCustomer?.customerCode ?? "VS",
    template: emailOptions[0].value,
    dailyChecks: false,
    midPeriodDate: null, //used when dailyChecks is true
    endPeriodDate: null, //used when dailyChecks is true
    operatorIds: "[]",
    flags: {
      forceLocalSend: false,
      ignoreGracePeriod: false,
      ignoreDataRecency: false,
      dryRun: false,
    },
  });

  const { data: operators, isPending: isPendingOperators } =
    AdminHooks.useGetOperators(auth.activeRole, formData.customerCode);

  const isShippingCustomer =
    customers.find((c) => c.customerCode === formData.customerCode)
      ?.industry === "shipping";

  // Current crew only is shipping-specific (shared vehicle inboxes) - drop it if it no longer applies.
  useEffect(() => {
    if (!isShippingCustomer && currentCrewOnly) {
      setCurrentCrewOnly(false);
    }
  }, [isShippingCustomer, currentCrewOnly]);

  const isNotRecentlySentMode = sendToMode === SEND_TO_MODES.NOT_RECENTLY_SENT;
  const { data: lastSentDates, isPending: isPendingLastSentDates } =
    AdminHooks.useGetLastSentDates(
      auth.activeRole,
      isNotRecentlySentMode ? formData.customerCode : undefined,
      isNotRecentlySentMode ? formData.template : undefined,
    );

  /** Only operators are eligible SEND TO recipients - deactivated ones never receive anything. */
  const activeOperators = React.useMemo(
    () => (operators ?? []).filter((o) => o.active !== false),
    [operators],
  );

  /** isCurrentCrew (shipping only) mirrors spitfire-reports-service's own "current crew" rnk = 1 filter:
   * for a shared vehicle inbox, only the most-recently-active operator per role is current - others
   * would be silently dropped when the report actually sends. Applying it here first keeps every mode
   * below (and the resolved-count preview) honest about who will actually receive anything. */
  const eligibleOperators = React.useMemo(
    () =>
      currentCrewOnly
        ? activeOperators.filter((o) => o.isCurrentCrew)
        : activeOperators,
    [activeOperators, currentCrewOnly],
  );

  /** roleAlias is per-operator metadata (see getCustomerCurrentOperatorsOnboard in admin_service) -
   * customers with a shared vehicle inbox can set a different value per crew member. Options are
   * derived from the selected customer's actual operators rather than a hardcoded list. */
  const roleAliasOptions = React.useMemo(() => {
    const distinctRoles = [
      ...new Set(eligibleOperators.map((o) => o.roleAlias).filter(Boolean)),
    ];
    return distinctRoles.map((role) => ({ value: role, text: role }));
  }, [eligibleOperators]);

  // Reset the selected role if it no longer applies (eg. after switching customer).
  useEffect(() => {
    if (
      selectedRoleAlias &&
      !roleAliasOptions.some((o) => o.value === selectedRoleAlias)
    ) {
      setSelectedRoleAlias("");
    }
  }, [roleAliasOptions, selectedRoleAlias]);

  /** Resolved operatorIds for the current sendToMode, or null while criteria/data aren't ready yet.
   * ALL only needs to compute an explicit list when currentCrewOnly is checked - otherwise "[]" (default
   * recipients) is left to spitfire-reports-service, which already applies its own current-crew filter. */
  const resolvedOperatorIds = React.useMemo(() => {
    switch (sendToMode) {
      case SEND_TO_MODES.ALL:
        return currentCrewOnly ? eligibleOperators.map((o) => o.id) : null;
      case SEND_TO_MODES.MANUAL:
        return null;
      case SEND_TO_MODES.REGISTERED:
        return eligibleOperators.filter((o) => !!o.appUserId).map((o) => o.id);
      case SEND_TO_MODES.UNREGISTERED:
        return eligibleOperators.filter((o) => !o.appUserId).map((o) => o.id);
      case SEND_TO_MODES.RECENT_EVENT_DATA: {
        if (!recentEventDataSince) return null;
        const threshold = new Date(recentEventDataSince);
        return eligibleOperators
          .filter(
            (o) => o.lastEventDate && new Date(o.lastEventDate) >= threshold,
          )
          .map((o) => o.id);
      }
      case SEND_TO_MODES.NOT_RECENTLY_SENT: {
        const days = Number(notRecentlySentDays);
        if (!lastSentDates || !days) return null;
        const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
        const lastSentAtByOperatorCode = new Map(
          lastSentDates.map((d) => [
            d.operatorCode,
            d.lastSentAt ? new Date(d.lastSentAt).getTime() : null,
          ]),
        );
        return eligibleOperators
          .filter((o) => {
            const lastSentAt = lastSentAtByOperatorCode.get(o.operatorCode);
            return !lastSentAt || lastSentAt < cutoff;
          })
          .map((o) => o.id);
      }
      case SEND_TO_MODES.ROLE: {
        if (!selectedRoleAlias) return null;
        return eligibleOperators
          .filter((o) => o.roleAlias === selectedRoleAlias)
          .map((o) => o.id);
      }
      default:
        return null;
    }
  }, [
    sendToMode,
    eligibleOperators,
    currentCrewOnly,
    recentEventDataSince,
    notRecentlySentDays,
    lastSentDates,
    selectedRoleAlias,
  ]);

  const isTrivialSendTo =
    sendToMode === SEND_TO_MODES.MANUAL ||
    (sendToMode === SEND_TO_MODES.ALL && !currentCrewOnly);

  // Push the resolved list into formData.operatorIds so the rest of the form (parsing, hint, submit)
  // keeps working unchanged. Manual mode is untouched - the admin edits formData.operatorIds directly.
  useEffect(() => {
    if (sendToMode === SEND_TO_MODES.ALL && !currentCrewOnly) {
      setFormData((prev) => ({ ...prev, operatorIds: "[]" }));
    } else if (sendToMode !== SEND_TO_MODES.MANUAL && resolvedOperatorIds) {
      setFormData((prev) => ({
        ...prev,
        operatorIds: JSON.stringify(resolvedOperatorIds),
      }));
    }
  }, [sendToMode, resolvedOperatorIds, currentCrewOnly]);

  const isSendToDataPending =
    !isTrivialSendTo &&
    (isPendingOperators || (isNotRecentlySentMode && isPendingLastSentDates));
  const isSendToReady =
    isTrivialSendTo || (!isSendToDataPending && resolvedOperatorIds !== null);

  /** Should match SnsTriggerReportDto from reports service */
  const formDataParsed = React.useMemo(() => {
    const parseOperatorIds = (input) => {
      if (!input) return [];

      try {
        const parsed = JSON.parse(input);
        if (!Array.isArray(parsed)) throw new Error("Not an array");

        const invalidId = parsed.find((id) => !AppUtils.isValidUUID(id));
        if (invalidId) throw new Error(`Invalid UUID detected: ${invalidId}`);

        return parsed;
      } catch (err) {
        showToast(
          err.message.includes("JSON")
            ? 'Operator IDs must be valid JSON (e.g. ["uuid1","uuid2"])'
            : err.message,
          "error",
        );
        return null;
      }
    };

    return {
      ...formData,
      operatorIds: parseOperatorIds(formData.operatorIds),
      midPeriodDate: formData.midPeriodDate
        ? new Date(formData.midPeriodDate)
        : null,
      endPeriodDate: formData.endPeriodDate
        ? new Date(formData.endPeriodDate)
        : null,
    };
  }, [formData, showToast]);

  // ---------- Real-time validation for hint ----------
  useEffect(() => {
    const input = formData.operatorIds;
    if (!input) {
      setOperatorIdsHint("");
      return;
    }
    try {
      const parsed = JSON.parse(input);
      if (!Array.isArray(parsed)) {
        setOperatorIdsHint("❌ Not a JSON array");
        return;
      }
      for (const id of parsed) {
        if (!AppUtils.isValidUUID(id)) {
          setOperatorIdsHint(`❌ Invalid UUID: ${id}`);
          return;
        }
      }
      setOperatorIdsHint("✅ Valid JSON");
    } catch {
      setOperatorIdsHint("❌ Invalid JSON");
    }
  }, [formData.operatorIds]);

  //---

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateFlag = (flag, value) => {
    setFormData((prev) => ({
      ...prev,
      flags: {
        ...prev.flags,
        [flag]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting, SnsTriggerReportDto:", formDataParsed);
    return await mutation
      .mutateAsync(formDataParsed)
      .then(() => showToast("Sent"))
      .catch((e) => showToast(`Something went wrong: ${e}`, "error"));
  };

  const customerOptions = customers
    .map((c) => ({
      ...c,
      value: c.customerCode,
      text: `${c.name} (${c.customerCode})`,
    }))
    .sort((a, b) => a.text.localeCompare(b.text));

  const renderFormTriggerReport = () => (
    <section className="p-4 space-y-3">
      <div>
        <h3>Template</h3>
        <DropDown
          options={emailOptions.map((o) => ({
            ...o,
            text: `[${o.value}] ${o.text}`,
          }))}
          value={formData.template}
          onChange={(val) => updateField("template", val)}
          enableSearch
        />
      </div>

      <div>
        <h3>Send To</h3>
        <DropDown
          options={sendToOptions}
          value={sendToMode}
          onChange={(val) => setSendToMode(val)}
        />

        {isShippingCustomer && sendToMode !== SEND_TO_MODES.MANUAL && (
          <Checkbox
            label={
              <div>
                Current crew only
                <div className="text-xs text-neutral-400">
                  Only the most-recently-active operator per role on each vessel
                  (eg. today&apos;s Captain, not a predecessor who has since
                  rotated off) - matches how spitfire-reports-service resolves
                  recipients internally, so nobody in the count below gets
                  silently dropped when this actually sends.
                </div>
              </div>
            }
            checked={currentCrewOnly}
            onChange={(e) => setCurrentCrewOnly(e.target.checked)}
          />
        )}

        {sendToMode === SEND_TO_MODES.RECENT_EVENT_DATA && (
          <label className="block mt-2">
            Event data more recent than
            <input
              type="date"
              value={recentEventDataSince}
              onChange={(e) => setRecentEventDataSince(e.target.value)}
              className="block p-2 mt-1 border border-neutral-400 rounded"
            />
          </label>
        )}

        {sendToMode === SEND_TO_MODES.NOT_RECENTLY_SENT && (
          <label className="block mt-2">
            Hasn&apos;t received this template in the last (days)
            <input
              type="number"
              min={1}
              value={notRecentlySentDays}
              onChange={(e) => setNotRecentlySentDays(e.target.value)}
              className="block p-2 mt-1 border border-neutral-400 rounded w-24"
            />
          </label>
        )}

        {sendToMode === SEND_TO_MODES.ROLE && (
          <div className="mt-2">
            {!isPendingOperators && roleAliasOptions.length === 0 ? (
              <div className="text-xs text-red-600">
                No roles are set on {formData.customerCode}&apos;s operators.
              </div>
            ) : (
              <label className="block">
                Role
                <div className="w-64">
                  <DropDown
                    options={roleAliasOptions}
                    value={selectedRoleAlias}
                    onChange={(val) => setSelectedRoleAlias(val)}
                  />
                </div>
              </label>
            )}
          </div>
        )}

        {!isTrivialSendTo && (
          <div className="text-xs text-neutral-400 mt-2">
            {isSendToDataPending && "Loading operators..."}
            {!isSendToDataPending && resolvedOperatorIds !== null && (
              <span>
                Matches {resolvedOperatorIds.length} of{" "}
                {eligibleOperators.length} eligible operator(s) for{" "}
                {formData.customerCode}
                {currentCrewOnly ? " (current crew only)" : ""}.
              </span>
            )}
            {!isSendToDataPending && resolvedOperatorIds === null && (
              <span>Set the criteria above to resolve recipients.</span>
            )}
          </div>
        )}

        {sendToMode === SEND_TO_MODES.MANUAL && (
          <div className="mt-2">
            <label>
              <h4>Operator IDs:</h4>
              <textarea
                rows={2}
                value={formData.operatorIds}
                onChange={(e) => updateField("operatorIds", e.target.value)}
                className="border p-1 mt-1 block w-full font-mono text-sm"
              />
            </label>
            <div className="text-xs text-neutral-400">
              Expects a JSON array of UUIDs, e.g.
              <code className="mx-1 bg-neutral-100">["uuid1","uuid2"]</code>
              <br />
              {operatorIdsHint && (
                <span
                  className={
                    operatorIdsHint.startsWith("✅")
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  {operatorIdsHint}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div>
        <h3>Flags:</h3>

        <Checkbox
          label={
            <div>
              Force Local Send
              <div className="text-xs text-neutral-400">
                Likely only useful for developers running locally, but left here
                just in case
              </div>
            </div>
          }
          checked={formData.flags.forceLocalSend}
          onChange={(e) => updateFlag("forceLocalSend", e.target.checked)}
        />

        <Checkbox
          label={
            <div>
              Ignore Grace Period
              <div className="text-xs text-neutral-400">
                Applies to some templates only (eg. Mid/End period, Welcome
                back, milestones)
              </div>
            </div>
          }
          checked={formData.flags.ignoreGracePeriod}
          onChange={(e) => updateFlag("ignoreGracePeriod", e.target.checked)}
        />

        <Checkbox
          label={
            <div>
              Ignore Data Recency
              <div className="text-xs text-neutral-400">
                Allow operators without recent data to receive notifications (1
                month + reporting delay)
              </div>
            </div>
          }
          checked={formData.flags.ignoreDataRecency}
          onChange={(e) => updateFlag("ignoreDataRecency", e.target.checked)}
        />

        <Checkbox
          label={
            <div>
              Dry Run
              <div className="text-xs text-neutral-400">
                Skip sending any notifications, instead Writes to a Log those
                who would have received any notification (The log is only
                viewable in AWS CloudWatch)
              </div>
            </div>
          }
          checked={formData.flags.dryRun}
          onChange={(e) => updateFlag("dryRun", e.target.checked)}
        />
      </div>
    </section>
  );
  const renderFormDailyChecks = () => (
    <section className="p-4 space-y-3">
      <h2>Daily Checks</h2>
      <div className="text-sm">
        <p>
          Check if any scheduled notifications need to be sent (eg. Kick-off,
          Mid/End period, Welcome Back).
        </p>
        <p>
          Normally run on a daily schedule, but re-triggering can be helpful if
          customer data was updated late.
        </p>
        <p>
          The "Goal Period Timeline" can help visualise what emails are expected
          to go out and/or which date to set as an override.
        </p>
      </div>
      <div>
        <label>
          Override Mid Period Date (optional)
          <input
            type="date"
            value={formData.midPeriodDate}
            onChange={(e) => updateField("midPeriodDate", e.target.value)}
            className="block p-2 mt-1 border border-neutral-400 rounded"
          />
        </label>
      </div>

      <div>
        <label>
          Override End Period Date (optional)
          <input
            type="date"
            value={formData.endPeriodDate}
            onChange={(e) => updateField("endPeriodDate", e.target.value)}
            className="block p-2 mt-1 border border-neutral-400 rounded"
          />
        </label>
      </div>
    </section>
  );

  return (
    <CardContainer padding={8}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-4 p-2">
          <h4>Select a customer:</h4>
          <div className="w-80">
            <DropDown
              options={customerOptions}
              value={formData.customerCode}
              onChange={(val) => updateField("customerCode", val)}
              enableSearch
            />
          </div>
          {formData.customerCode && (
            <ImageWithRetry
              src={CUSTOMER_LOGO_URL(formData.customerCode)}
              alt={`${formData.customerCode} logo`}
              className="h-12 w-auto object-contain"
            />
          )}
        </div>

        <Tabs
          tabs={[
            {
              name: "Trigger a Report",
              colour: "primary",
              content: renderFormTriggerReport(),
            },
            {
              name: "Run Daily Checks",
              colour: "primary",
              content: renderFormDailyChecks(),
            },
          ]}
          onChange={(idx) => {
            setFormData({
              ...formData,
              dailyChecks: idx === 1,
            });
          }}
        />

        <Button
          submit
          icon="bellOutline"
          loading={mutation.isPending}
          disabled={!formData.dailyChecks && !isSendToReady}
        >
          Send
        </Button>
      </form>

      <pre className="text-xs bg-neutral-100 p-2">
        {JSON.stringify(formDataParsed, null, 2)}
      </pre>
    </CardContainer>
  );
}

export default ReportsServiceSnsForm;
